import { Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import {
  CancelOutlined,
  CheckCircleOutline,
  EventBusyOutlined,
  Inventory2Outlined,
  LocalOfferOutlined,
  QrCode2,
  ScheduleOutlined,
  SimCardOutlined,
  TravelExploreOutlined,
} from '@mui/icons-material';

import { EsimStatus, EsimType } from '@models/stock';
import { getEsimStatusLabel } from '@constants/stockLabels';

const iconSx = {
  fontSize: 16,
  flex: '0 0 auto',
} as const;

const fallbackText = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const InlineMetaDisplay = ({
  icon,
  label,
  color,
}: {
  icon: ReactNode;
  label: string;
  color: string;
}) => (
  <Stack
    component="span"
    direction="row"
    spacing={0.75}
    alignItems="center"
    sx={{
      color,
      minWidth: 0,
    }}
  >
    {icon}
    <Typography
      component="span"
      variant="body2"
      sx={{
        color: 'inherit',
        fontWeight: 800,
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </Stack>
);

const getEsimTypeDisplayMeta = (type: unknown) => {
  const key = String(type ?? '').trim();

  switch (key) {
    case EsimType.LPA:
      return {
        label: 'LPA',
        color: 'info.main',
        icon: <QrCode2 sx={iconSx} />,
      };
    case EsimType.REDEMPTION:
      return {
        label: 'Coupon',
        color: 'success.main',
        icon: <LocalOfferOutlined sx={iconSx} />,
      };
    case EsimType.DISCOVERY:
      return {
        label: 'Discovery',
        color: 'secondary.main',
        icon: <TravelExploreOutlined sx={iconSx} />,
      };
    case EsimType.PSIM_TOPUP:
      return {
        label: 'Topup',
        color: 'warning.main',
        icon: <SimCardOutlined sx={iconSx} />,
      };
    default:
      return {
        label: key || '-',
        color: 'text.secondary',
        icon: null,
      };
  }
};

export const EsimTypeDisplay = ({ type }: { type: unknown }) => {
  const meta = getEsimTypeDisplayMeta(type);
  return <InlineMetaDisplay icon={meta.icon} label={meta.label} color={meta.color} />;
};

const getEsimStatusDisplayMeta = (status: unknown) => {
  const key = String(status ?? '').trim();
  const label = key ? getEsimStatusLabel(key) : fallbackText(status);

  switch (key) {
    case EsimStatus.READY:
      return {
        label,
        color: 'success.dark',
        icon: <CheckCircleOutline sx={iconSx} />,
      };
    case EsimStatus.SHIPPED:
      return {
        label,
        color: 'text.secondary',
        icon: <Inventory2Outlined sx={iconSx} />,
      };
    case EsimStatus.PENDING:
      return {
        label,
        color: 'warning.main',
        icon: <ScheduleOutlined sx={iconSx} />,
      };
    case EsimStatus.EXPIRED:
      return {
        label,
        color: 'secondary.main',
        icon: <EventBusyOutlined sx={iconSx} />,
      };
    case EsimStatus.CANCELED:
      return {
        label,
        color: 'error.main',
        icon: <CancelOutlined sx={iconSx} />,
      };
    default:
      return {
        label,
        color: 'text.secondary',
        icon: null,
      };
  }
};

export const EsimStatusDisplay = ({ status }: { status: unknown }) => {
  const meta = getEsimStatusDisplayMeta(status);
  return <InlineMetaDisplay icon={meta.icon} label={meta.label} color={meta.color} />;
};
